import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { KyrgyzstanFiltersWidget } from 'widgets/kyrgyzstan/filters'

import {
  KyrgyzstanFiltersState,
  defaultPriceRange,
  kyrgyzstanSearchStore,
} from 'entities/kyrgyzstan/model'

import { simpleIsEqual } from 'shared/lib/simpleIsEqual'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderResetButton } from 'shared/ui/header-reset-button'

import s from './styles.module.scss'

const KyrgyzstanFiltersPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const defaultState = {
    realtyTypeIds: undefined,
    priceRange: defaultPriceRange,
    starRatings: undefined,
  }

  const initialState = useMemo<KyrgyzstanFiltersState>(
    () => ({
      realtyTypeIds:
        kyrgyzstanSearchStore.realty_type_ids || defaultState.realtyTypeIds,
      priceRange: [
        kyrgyzstanSearchStore.price_min ??
          kyrgyzstanSearchStore.availableMinPrice,
        kyrgyzstanSearchStore.price_max ??
          kyrgyzstanSearchStore.availableMaxPrice,
      ],
      starRatings: defaultState.starRatings,
    }),
    [],
  )

  const [filters, setFilters] = useState<KyrgyzstanFiltersState>(initialState)

  const isCanReset = useMemo(() => {
    return !simpleIsEqual(defaultState, filters)
  }, [defaultState, filters])

  const hasActiveFilters = useMemo(() => {
    return !simpleIsEqual(initialState, filters)
  }, [initialState, filters])

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleReset = () => {
    setFilters({
      realtyTypeIds: undefined,
      priceRange: [
        kyrgyzstanSearchStore.availableMinPrice,
        kyrgyzstanSearchStore.availableMaxPrice,
      ],
      starRatings: undefined,
    })
    kyrgyzstanSearchStore.resetFilters()
    kyrgyzstanSearchStore.setPriceRange(undefined, undefined)
    kyrgyzstanSearchStore.reset()
  }

  const handleApply = () => {
    kyrgyzstanSearchStore.reset()
    kyrgyzstanSearchStore.resetFilters()

    kyrgyzstanSearchStore.setRealtyTypeIds(filters.realtyTypeIds)

    const [minSelected, maxSelected] = filters.priceRange
    if (
      minSelected !== kyrgyzstanSearchStore.availableMinPrice ||
      maxSelected !== kyrgyzstanSearchStore.availableMaxPrice
    ) {
      kyrgyzstanSearchStore.setPriceRange(minSelected, maxSelected)
    } else {
      kyrgyzstanSearchStore.setPriceRange(undefined, undefined)
    }

    navigate(-1)
  }

  const handleFiltersChange = (updates: Partial<KyrgyzstanFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...updates }))
  }

  return (
    <div className={s.container}>
      <HeaderResetButton
        title={t('filter')}
        onBack={handleGoBack}
        onReset={handleReset}
        isIconBlack
        isCanDisable={isCanReset}
      />

      <KyrgyzstanFiltersWidget
        className="container"
        filters={filters}
        minPrice={kyrgyzstanSearchStore.availableMinPrice}
        maxPrice={kyrgyzstanSearchStore.availableMaxPrice}
        onFiltersChange={handleFiltersChange}
      />

      <ButtonContinue
        onClick={handleApply}
        disabled={!hasActiveFilters}
      >
        <span className="semi-bold-17">{t('apply')}</span>
      </ButtonContinue>
    </div>
  )
})

export default KyrgyzstanFiltersPage
